import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { LuDownload, LuEllipsisVertical } from "react-icons/lu";
import { saveAs } from "file-saver";
import { BeatLoader } from "react-spinners";
import { IApplication } from "../types";
import { getAllApplicationsByAdmin } from "../services/applicationService";
import { Table } from "../components/table/table";
import ApplicationDetailsModal from "../components/modals/application-details";

const LuDownloadIcon = LuDownload as React.ElementType;
const LuEllipsisVerticalIcon = LuEllipsisVertical as React.ElementType;

interface ApplicationsProps {
  searchQuery?: string;
}

const Applications: React.FC<ApplicationsProps> = ({ searchQuery }) => {
  const {
    data: applications,
    isLoading,
    error,
  } = useQuery<IApplication[]>({
    queryKey: ["applications", searchQuery],
    queryFn: () => getAllApplicationsByAdmin(searchQuery),
  });

  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] =
    useState<IApplication | null>(null);

  const toggleDropdown = (id: string) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const handleView = (id: string) => {
    const application = applications?.find((app) => app._id === id);
    if (application) {
      setSelectedApplication(application);
    }
    setDropdownOpen(null);
  };

  const downloadCSV = () => {
    if (!applications || applications.length === 0) return;

    const headers = ["User", "Email", "Post ID", "Status", "Created At"];

    const rows = applications.map((app) => [
      `"${app.userId?.lastName ?? "N/A"} ${app.userId?.firstName ?? "N/A"}"`,
      `"${app.userId?.email ?? "N/A"}"`,
      `"${app.postId}"`,
      `"${app.status}"`,
      `"${new Date(app.createdAt).toLocaleDateString()}"`,
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "applications.csv");
  };

  const columns = [
    {
      header: "User",
      accessorKey: "user",
      cell: ({ row }: { row: { original: IApplication } }) => {
        const user = row.original.userId;
        return user ? (
          <p className="text-sm font-medium text-gray-800">
            {user.lastName} {user.firstName}
          </p>
        ) : (
          <p className="text-sm text-gray-600">N/A</p>
        );
      },
    },
    {
      header: "Email",
      accessorKey: "email",
      cell: ({ row }: { row: { original: IApplication } }) => {
        const user = row.original.userId;
        return user ? (
          <p className="text-sm text-gray-600">{user.email}</p>
        ) : (
          <p className="text-sm text-gray-600">N/A</p>
        );
      },
    },
    {
      header: "Post ID",
      accessorKey: "jobId",
      cell: ({ row }: { row: { original: IApplication } }) => (
        <p className="text-sm text-gray-700">{row.original.postId}</p>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }: { row: { original: IApplication } }) => (
        <span
          className={`px-2 py-1 rounded-full text-sm capitalize ${
            row.original.status === "accepted"
              ? "text-emerald-500"
              : row.original.status === "pending"
              ? "text-amber-500"
              : "text-rose-500"
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }: { row: { original: IApplication } }) => (
        <p className="text-sm text-gray-600">
          {new Date(row.original.createdAt).toLocaleDateString()}
        </p>
      ),
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IApplication } }) => (
        <div className="relative">
          <button
            onClick={() => toggleDropdown(row.original._id)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <LuEllipsisVerticalIcon className="text-gray-600 text-lg" />
          </button>
          {dropdownOpen === row.original._id && (
            <div
              className="absolute right-0 mt-2 w-24 bg-white shadow-md border rounded-md z-10"
              onClick={() => handleView(row.original._id)}
            >
              <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">
                View
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".relative")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );

  if (error)
    return <p className="text-rose-500 text-sm">Error loading applications.</p>;

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Applications</h2>
        <div className="flex items-center gap-2">
          <div
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center justify-between space-x-2 text-indigo-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </div>
        </div>
      </div>
      <Table columns={columns} data={applications || []} />

      {selectedApplication && (
        <ApplicationDetailsModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
        />
      )}
    </div>
  );
};

export default Applications;
